import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FormLoader,
  Form,
  useFormErrors,
  Input,
} from "../../commons/Form/Form";
import {
  SettingList,
  Setting,
  SettingDisplay,
  SettingLocker,
  SettingModder,
  VerifyPasswordModal,
} from "../Common/Settings";
import authAPI from "../../../api/authentication";
import api from "../../../api/resources";
import { mapAuthErrors } from "../../../api/authentication";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import { useToastsDispatch } from "../../commons/Toasts/ToastsContext";
import { availableLanguages } from "../../../i18n";
import LangSwitch from "../../commons/LangSwitch/LangSwitch";
import { useAuth } from "../../../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import routes from "../../../routes/routes";
import Button from "../../commons/Button/Button";

export default function Profile() {
  const { t } = useTranslation(["common", "settings"]);
  const dispatchModal = useModalDispatch();
  document.title = t("settings:tabs.profile.title");
  const [loading, setLoading] = useState(true);

  const keys = ["name", "surname", "language", "telephone", "email"];
  const initialUser = Object.fromEntries(keys.map((key) => [key, ""]));
  const [user, setUser] = useState(initialUser);

  const init = async () => {
    const response = await authAPI.getUserInfo();
    if (response.status !== 200) return;
    setUser(response.data.user);
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const labelMap = { name: "first_name" };

  const languageDisplay = useLanguageDisplay(user.language);

  const renderComponent = (key, { label, value }) => {
    const components = {
      language: <LangSwitch />,
      telephone: (
        <SettingModder
          modal={{
            component: <PhoneForm label={label} initValue={value} />,
            onSuccess: init,
          }}
        />
      ),
      email: (
        <SettingModder
          modal={{
            title: t("settings:modals.verifyPassword.title"),
            message: t("settings:modals.verifyPassword.message"),
            component: (
              <VerifyPasswordModal
                onNextModal={() =>
                  dispatchModal({
                    type: "add",
                    title: t("common:ModifyTitle"),
                    component: <EmailForm label={label} initialValue={value} />,
                    onSuccess: init,
                  })
                }
              />
            ),
          }}
        />
      ),
    };

    return components[key] || <SettingLocker />;
  };

  if (loading) return <FormLoader />;

  return (
    <div>
      <SettingList>
        {keys.map((key) => {
          const label = t(`common:${labelMap[key] ?? key}`);
          const displayValue = key === "language" ? languageDisplay : user[key];

          return (
            <Setting key={key} id={key} testid={key}>
              <SettingDisplay testid={key} label={label} value={displayValue} />
              {renderComponent(key, { label, value: user[key] })}
            </Setting>
          );
        })}
      </SettingList>
      <div style={{ marginTop: "1rem" }}>
        <AccountDel />
      </div>
    </div>
  );
}

function PhoneForm({ label, initValue }) {
  const dispatchModal = useModalDispatch();

  const [value, setValue] = useState(initValue.replaceAll(" ", ""));

  const formErrorKey = "non_field_errors";
  const inputKeys = ["telephone"];
  const { errors, setErrors, clearErrors } = useFormErrors(inputKeys);

  const handleSubmit = async () => {
    clearErrors();
    const response = await api.updateUser({ telephone: value });
    if (response.ok) {
      dispatchModal({ type: "success" });
    } else {
      const errors = await response.json();
      setErrors(errors);
    }
  };

  const inputs = [
    {
      label: label,
      name: "telephone",
      type: "tel",
      value: value && value.replaceAll(" ", ""),
      onChange: setValue,
      errors: errors.telephone,
      required: true,
    },
  ];

  const form = {
    inputs: [
      {
        data: inputs,
      },
    ],
    onSubmit: handleSubmit,
    errors: errors[formErrorKey],
  };

  return <Form {...form} />;
}

function EmailForm({ label, initialValue }) {
  const [value, setValue] = useState(initialValue);

  const dispatchToast = useToastsDispatch();
  const dispatchModal = useModalDispatch();
  const { t } = useTranslation(["common", "settings"]);

  const formErrorKey = "non_field_errors";
  const inputKeys = ["email"];
  const { errors, setErrors, clearErrors } = useFormErrors(inputKeys);

  const handleSubmit = async () => {
    clearErrors();

    const response = await authAPI.changeEMailAddress(value);
    if (response.ok) {
      dispatchToast({
        type: "add",
        variant: "info",
        heading: t("settings:toasts.confirmEmail.heading"),
        subheading: t("settings:toasts.confirmEmail.subheading"),
      });
      dispatchModal({ type: "success" });
    } else {
      const data = await response.json();
      const errorsObject = mapAuthErrors(data.errors);
      setErrors(errorsObject);
    }
  };

  const inputs = [
    {
      label: label,
      type: "email",
      name: "email",
      value: value,
      onChange: setValue,
      errors: errors.email,
    },
  ];

  const form = {
    inputs: [
      {
        data: inputs,
      },
    ],
    onSubmit: handleSubmit,
    errors: errors[formErrorKey],
  };

  return <Form {...form} />;
}

function AccountDel() {
  const { t } = useTranslation(["common", "settings"]);
  const label = t("settings:items.deleteAccount.label");

  const { onLogout } = useAuth();
  const navigate = useNavigate();
  const dispatchModal = useModalDispatch();

  const handleSuccess = async () => {
    const response = await onLogout();
    if (response.ok) navigate(routes.Home);
  };

  const modal = {
    title: t("settings:modals.deleteAccount.title"),
    message: t("settings:modals.deleteAccount.message"),
    component: <DeleteAccountForm />,
    onSuccess: () => handleSuccess(),
  };

  return (
    <Button
      id="delete-account"
      variant="link"
      text={label}
      onClick={() => dispatchModal({ type: "add", ...modal })}
    />
  );
}

function DeleteAccountForm() {
  const dispatchModal = useModalDispatch();
  const { t } = useTranslation(["common", "settings"]);
  const [reason, setReason] = useState(null);
  const inputKeys = ["reason"];
  const formErrorKey = "non_field_errors";
  const { errors, setErrors, clearErrors } = useFormErrors(
    inputKeys,
    formErrorKey,
  );

  const i18nKey = "settings:modals.deleteAccount.component.form";

  const handleDeleteAccount = async () => {
    clearErrors();

    const response = await api.deleteAccount(reason.value);

    if (!response.ok) {
      setErrors({
        [formErrorKey]: [t(i18nKey + ".select.error")],
      });
      return;
    }
    setReason(null);
    dispatchModal({ type: "success" });
  };

  const options = ["used", "complicated", "useless", "expensive", "other"].map(
    (value) => ({
      value,
      label: t(`${i18nKey}.select.options.${value}.label`),
    }),
  );

  const reasonInput = {
    label: t(i18nKey + ".select.label"),
    name: "reason",
    type: "radio",
    value: reason,
    onChange: setReason,
    errors: errors.reason,
    options: options,
  };

  return (
    <Form onSubmit={handleDeleteAccount} errors={errors[formErrorKey]}>
      <Input {...reasonInput} />
    </Form>
  );
}

function useLanguageDisplay(userLanguage) {
  const [languageDisplay, setLanguageDisplay] = useState("");
  const { i18n } = useTranslation();

  const updateLanguageDisplay = () =>
    setLanguageDisplay(availableLanguages[i18n.resolvedLanguage]);

  useEffect(() => {
    updateLanguageDisplay();
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    if (userLanguage && availableLanguages[userLanguage]) {
      setLanguageDisplay(availableLanguages[userLanguage]);
    }
  }, [userLanguage]);

  return languageDisplay;
}
